import { Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import {
    GameAdminCommentCategory,
    GameAdminCommentDto,
    UpdateGameAdminCommentDto,
} from '../../../models/game/dto/game-admin-comment.dto';
import { useUpdateGameAdminComment } from '../hooks/useGameAdminComments';
import { categoryLabels } from './adminCommentConstants';

interface EditAdminCommentModalProps {
    isOpen: boolean;
    onClose: () => void;
    comment: GameAdminCommentDto;
    gameId: number;
}

const categoryOptions = Object.values(GameAdminCommentCategory).map((cat) => ({
    value: cat,
    label: categoryLabels[cat],
}));

export default function EditAdminCommentModal({
    isOpen,
    onClose,
    comment,
    gameId,
}: EditAdminCommentModalProps) {
    const [content, setContent] = useState('');
    const [category, setCategory] = useState<GameAdminCommentCategory | ''>('');

    const updateCommentMutation = useUpdateGameAdminComment(gameId);

    // Initialize form with comment data when modal opens
    useEffect(() => {
        if (isOpen && comment) {
            setContent(comment.content);
            setCategory(comment.category || '');
        }
    }, [isOpen, comment]);

    const handleSubmit = () => {
        if (!content.trim()) return;

        const updateData: UpdateGameAdminCommentDto = {
            content: content.trim(),
            category: category || undefined,
        };

        updateCommentMutation.mutate(
            { commentId: comment.id, data: updateData },
            {
                onSuccess: () => {
                    onClose();
                },
            }
        );
    };

    const handleClose = () => {
        // Reset form to original values
        setContent(comment.content);
        setCategory(comment.category || '');
        onClose();
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            title={translate('admin_comments.edit_comment')}
            onSubmit={handleSubmit}
            submitLabel={translate('admin_comments.save_changes')}
            cancelLabel={translate('common.cancel')}
            isSubmitting={updateCommentMutation.isLoading}
            isSubmitDisabled={!content.trim()}
            size="md"
        >
            <FormStack>
                <FormField
                    type="select"
                    label={translate('admin_comments.category')}
                    value={category}
                    onChange={(value) => setCategory(value as GameAdminCommentCategory)}
                    options={categoryOptions}
                    placeholder={translate('admin_comments.select_category')}
                />

                <FormField
                    type="textarea"
                    label={translate('admin_comments.content')}
                    value={content}
                    onChange={setContent}
                    placeholder={translate('admin_comments.content_placeholder')}
                    isRequired
                    textareaProps={{ rows: 6, resize: 'vertical' }}
                />

                {category === GameAdminCommentCategory.PAYMENT_EXTERNAL && (
                    <Text fontSize="sm" color="orange.600" bg="orange.50" p={3} borderRadius="md">
                        💡 {translate('admin_comments.payment_external_tip')}
                    </Text>
                )}
            </FormStack>
        </FormModal>
    );
}
